import { nextTick } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

/**
 * When the workbook reaches the completion passage, mark the attempt complete and finish the flow.
 * @param {object} [options]
 * @param {import('vuex').Store} [options.store]
 * @param {import('vue-router').Router} [options.router]
 * @param {() => object | null} [options.getLabManifest] Override manifest source; defaults to Vuex `labManifest` getter.
 * @param {() => Promise<void>} [options.reinitializeStory] `initializeStory` from useStoryEngine (avoid circular imports).
 */
export function useLabCompletion(options = {}) {
  const store = options.store ?? useStore();
  const router = options.router ?? useRouter();
  const getLabManifest =
    options.getLabManifest ?? (() => store.getters.labManifest);
  const reinitializeStory = options.reinitializeStory ?? (async () => {});

  function checkLabCompletion(context) {
    const manifest = getLabManifest();
    const doneId = manifest?.completionPassageId ?? "lab-complete";
    const currentLabAttempt = store.getters.getCurrentLabAttempt;
    if (
      context?.current === doneId &&
      currentLabAttempt &&
      !currentLabAttempt.isComplete
    ) {
      store.dispatch("completeCurrentLabAttempt");

      if (currentLabAttempt) {
        store.dispatch("updateLabAttempt", {
          attemptId: currentLabAttempt.attemptId,
          updates: {
            formData: context?.flags || {},
            isComplete: true
          }
        });
      }

      nextTick(() => {
        showLabCompletionDialog();
      });
    }
  }

  async function showLabCompletionDialog() {
    const locksOnCompletion = store.getters.locksOnCompletion;

    try {
      if (locksOnCompletion && store.getters.canStartNewAttempt) {
        // Locking labs: create a fresh attempt slot so the student can start over.
        await store.dispatch("startNewLabAttempt");
      }
      // Always reset workbook context so the story can restart from the beginning.
      store.dispatch("setContext", null);
      await nextTick();
      await reinitializeStory();
    } catch (error) {
      console.error("Error completing lab:", error);
      alert(
        "There was an issue finalizing the lab. Please check the Lab Attempts page."
      );
    }

    alert(
      "Lab completed successfully!\n\nYou will now be redirected to the Lab Attempts page."
    );

    try {
      await router.push({ name: "LabAttempts" });
    } catch (error) {
      console.error("Navigation error:", error);
      alert("Unable to navigate to Lab Attempts. Please try again.");
    }
  }

  return {
    checkLabCompletion,
    showLabCompletionDialog
  };
}
